import React, { useContext, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import "./FileUpload.css";
import { IconButton } from "@mui/material";
import { GlobalState } from "../../context/GlobalContext";
import Vimeo from "@u-wave/react-vimeo";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { Document, Page } from 'react-pdf';
import '../../utils/pdfjs-config';  // Updated import path

const checkFileType = (fileUrl) => {
  // Define arrays of image and video extensions
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];
  const videoExtensions = ['.mp4', '.avi', '.webm', '.mov', '.ogv'];

  // Get the lowercase version of the URL
  let lowerUrl;
  if (typeof fileUrl === "string") {
    lowerUrl = fileUrl?.toLowerCase();
    // Check for image extensions
    if (imageExtensions.some(ext => lowerUrl.endsWith(ext))) {
      return 'image';
    }
  
    // Check for video extensions
    if (videoExtensions.some(ext => lowerUrl.endsWith(ext))) {
      return 'video';
    }
  } else {
    lowerUrl = fileUrl?.url?.toLowerCase();
  }

  return ""
}

const CssCircularProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  position: "relative",
  top: "8px",
  right: "8px",
}));

function isVimeoUrl(url) {
  const vimeoRegex = /^(https?:\/\/)?(www\.)?(vimeo\.com\/)([0-9]{9})(.*)?$/;
  return vimeoRegex.test(url);
}

const ImagePreview = (props) => {
  const { isPublished } = useContext(GlobalState);
  const {
    imgPreview,
    fileData,
    imgPath,
    isSpinnerEnabled,
    onImageRemove,
    onImageView,
    onPlayVideo,
    onMusicVideo,
    accept,
    onClose,
    shouldOpenPdf = false
  } = props;
  const hasVideoAccept = Object.keys(accept || {}).some((k) => k.startsWith("video/"));
  const isYoutube = typeof imgPreview === "string" && /(youtube\.com|youtu\.be)/i.test(imgPreview);

  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    // Handle URL encoding for Azure Blob Storage URLs
    if (imgPreview && isPdfUrl(imgPreview)) {
      try {
        // Decode the URL first to handle any encoded characters
        const decodedUrl = decodeURIComponent(imgPreview);
        // Re-encode the URL properly
        const encodedUrl = encodeURI(decodedUrl);
        
        setPdfUrl(encodedUrl);
      } catch (error) {
        console.error('Error processing PDF URL:', error);
        setPdfError('Error processing PDF URL');
      }
    }
  }, [imgPreview]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPdfError('Failed to load PDF. Please try again.');
  };

  // Function to check if the URL is a PDF
  const isPdfUrl = (url) => {
    if (!url) return false;
    const lowerUrl = url.toLowerCase();
    return lowerUrl.endsWith('.pdf') || 
           lowerUrl.includes('pdf') || 
           (fileData?.type && fileData.type.includes('pdf'));
  };

  if (isYoutube) {
    return (
      <div className="image-container" style={{ flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "12px", wordBreak: "break-all" }}>
        <div style={{ color: "#FFF", textAlign: "center" }}>{imgPreview}</div>
        {isPublished ? null : (
          <IconButton style={{ color: "#FFF" }} onClick={onImageRemove}>
            <DeleteIcon style={{ fontSize: "30px" }} />
          </IconButton>
        )}
      </div>
    );
  }

  return (
    <>
      {accept["image/*"] && (fileData?.type?.includes("image") || checkFileType(imgPreview) === "image") && (
        <div className="image-container">
          <img src={imgPreview} alt={imgPath} className="image" />
          <div className="overlay">
            {isSpinnerEnabled && <CssCircularProgress size={24} />}
            {isPublished ? null : (
              <IconButton style={{ color: "#FFF" }} onClick={onImageRemove}>
                <DeleteIcon style={{ fontSize: "30px" }} />
              </IconButton>
            )}
            <IconButton style={{ color: "#FFF" }} onClick={onImageView}>
              <VisibilityOutlinedIcon style={{ fontSize: "30px" }} />
            </IconButton>
          </div>
        </div>
      )}
      {hasVideoAccept && (fileData?.type?.includes("video") || checkFileType(imgPreview) === "video") && (
        <div className="image-container">
          <video src={imgPreview} alt={imgPath} className="image" />
          <div className="overlay">
            {isSpinnerEnabled && <CssCircularProgress size={24} />}
            {isPublished ? null : (
              <IconButton style={{ color: "#FFF" }} onClick={onImageRemove}>
                <DeleteIcon style={{ fontSize: "30px" }} />
              </IconButton>
            )}
            <IconButton style={{ color: "#FFF" }} onClick={(e)=>onPlayVideo(e,"video")}>
              <PlayCircleOutlineIcon style={{ fontSize: "30px" }} />
            </IconButton>
          </div>
        </div>
      )}
      {accept["audio/*"] && (
        <div className="image-container">
          <img src={"/images/AudioImage.jpg"} alt={imgPath} className="image" />
          <div className="overlay">
            {isSpinnerEnabled && <CssCircularProgress size={24} />}
            {isPublished ? null : (
              <IconButton style={{ color: "#FFF" }} onClick={onImageRemove}>
                <DeleteIcon style={{ fontSize: "30px" }} />
              </IconButton>
            )}
            <IconButton style={{ color: "#FFF" }} onClick={(e)=>onMusicVideo(e, "audio")}>
              <PlayCircleOutlineIcon style={{ fontSize: "30px" }} />
            </IconButton>
          </div>
        </div>
      )}
      {isVimeoUrl(imgPreview) && (
        <div className="image-container" style={{ height: "100%"}} >
          <Vimeo
            video={imgPreview}
            autoplay
            preload
            start={0}
            responsive
            style={{
              height: "100%",
              padding: "0 !important",
              position: "static !important",
            }}
          />
          <div className="overlay">
            {isSpinnerEnabled && <CssCircularProgress size={24} />}
            {isPublished ? null : (
              <IconButton style={{ color: "#FFF", marginRight: "45px", background: 'black', borderRadius: '6px', marginTop: "8px", padding: '6px' }} onClick={onImageRemove}>
                <DeleteIcon style={{ fontSize: "20px" }} />
              </IconButton>
            )}
          </div>
        </div>
      )}
      {(accept["application/pdf"] || accept["pdf"]) && (
        (isPdfUrl(imgPreview) || (fileData?.type && fileData.type.includes("pdf")))
      ) && (
        <div className="image-container">
          <div style={{ width: '100%', height: "100%", background: '#f5f5f5', display: 'flex', justifyContent: 'center' ,overflow: 'auto'}}>
            {pdfError ? (
              <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                {pdfError}
              </div>
            ) : (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress />
                    <span style={{ marginTop: '10px', color: '#666' }}>Loading PDF...</span>
                  </div>
                }
                error={
                  <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                    {pdfError || 'Failed to load PDF. Please try again.'}
                  </div>
                }
                // options={{
                //   cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
                //   cMapPacked: true,
                //   standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
                // }}
              >
                <Page 
                  pageNumber={1} 
                  width={400}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  canvasBackground="transparent"
                />
              </Document>
            )}
          </div>
          <div className="overlay">
            {isSpinnerEnabled && <CssCircularProgress size={24} />}
            {isPublished ? null : (
              <IconButton style={{ color: "#FFF" }} onClick={onImageRemove}>
                <DeleteIcon style={{ fontSize: "30px" }} />
              </IconButton>
            )}
            <IconButton style={{ color: "#FFF" }} onClick={() => setOpenPdfModal(true)}>
              <VisibilityOutlinedIcon style={{ fontSize: "30px" }} />
            </IconButton>
          </div>
          <Modal
            open={shouldOpenPdf || openPdfModal}
            onClose={() => shouldOpenPdf ? onClose() : setOpenPdfModal(false)}
            aria-labelledby="pdf-modal-title"
            aria-describedby="pdf-modal-description"
          >
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90vw',
                height: '90vh',
                bgcolor: 'background.paper',
                boxShadow: 24,
                p: 2,
                outline: 'none',
                overflow: 'auto'
              }}
            >
              <Document
                file={pdfUrl || imgPreview}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <CircularProgress />
                    <span style={{ marginTop: '10px' }}>Loading PDF...</span>
                  </div>
                }
                error={
                  <div style={{ color: 'red', textAlign: 'center', padding: '20px' }}>
                    {pdfError || 'Failed to load PDF. Please try again.'}
                  </div>
                }
                // options={{
                //   cMapUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/cmaps/',
                //   cMapPacked: true,
                //   standardFontDataUrl: 'https://unpkg.com/pdfjs-dist@3.11.174/standard_fonts/',
                //   // withCredentials: true,
                // }}
              >
                {Array.from(new Array(numPages), (el, index) => (
                  <Page 
                    key={`page_${index + 1}`} 
                    pageNumber={index + 1} 
                    width={window.innerWidth * 0.8}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                ))}
              </Document>
            </Box>
          </Modal>
        </div>
      )}
    </>
  );
};

export default ImagePreview;
